import React from "react";
import "./style.css";
import { Textarea } from "../../../components/ui/textarea";

/**
 * @typedef {Object} InvoiceItem
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {number} rate
 * @property {number} qty
 * @property {number} amount
 * @property {boolean} optional
 */

/**
 * @param {{
 * 	items: InvoiceItem[],
 * 	onItemsChange: (items: InvoiceItem[]) => void
 * }} props
 */
export default function InvoiceItems({ items, onItemsChange }) {
	const addItem = () => {
		const newItem = {
			id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
			name: "",
			description: "",
			rate: 0,
			qty: 1,
			amount: 0,
			optional: false
		};

		onItemsChange([...(items || []), newItem]);
	};

	const removeItem = (id) => {
		if ((items || []).length > 1) {
			onItemsChange(items.filter((item) => item.id !== id));
		}
	};

	const updateItem = (id, field, value) => {
		const updatedItems = (items || []).map((item) => {
			if (item.id !== id) return item;

			const updatedItem = { ...item, [field]: value };

			if (field === "rate" || field === "qty") {
				const rate = Number(updatedItem.rate) || 0;
				const qty = Number(updatedItem.qty) || 0;
				updatedItem.amount = rate * qty;
			}

			return updatedItem;
		});

		onItemsChange(updatedItems);
	};

	const toggleOptional = (id) => {
		const updatedItems = (items || []).map((item) => {
			if (item.id !== id) return item;
			return { ...item, optional: !item.optional };
		});

		onItemsChange(updatedItems);
	};

	return (
		<div className="invoice-section">
			<h3 className="invoice-section-title">Itemised Fees</h3>

			<div className="table-wrap">
				<table className="invoice-table">
					<thead>
						<tr className="invoice-table__head">
							<th className="th th--name">Name</th>
							<th className="th">Description</th>
							<th className="th th--num">Rate ($)</th>
							<th className="th th--num th--qty">Qty</th>
							<th className="th th--num th--amount">Amount ($)</th>
							<th className="th th--actions" aria-label="Actions" />
						</tr>
					</thead>

					<tbody>
						{(items || []).map((item, index) => (
							<tr
								key={item.id}
								className={`invoice-table__row ${item.optional ? "is-optional" : ""}`}
								style={{ animationDelay: `${index * 50}ms` }}
							>
								<td className="td">
									<div className="item-name-wrap">
										<input
											className="invoice-input item-name"
											type="text"
											placeholder="Item name"
											value={item.name || ""}
											onChange={(e) => updateItem(item.id, "name", e.target.value)}
										/>

										<button
											type="button"
											className={`opt-toggle ${item.optional ? "opt-toggle--on" : ""}`}
											onClick={() => toggleOptional(item.id)}
											aria-label={item.optional ? "Mark as required" : "Mark as optional"}
											title={item.optional ? "Optional item (click to make required)" : "Required item (click to make optional)"}
										>
											{item.optional ? "Optional" : "Required"}
										</button>

										{/* Printed/PDF badge (shows in preview even if buttons get flattened) */}
										{item.optional ? <span className="opt-badge">Optional</span> : null}
									</div>
								</td>

								<td className="td">
									<Textarea
										className="invoice-input invoice-textarea"
		placeholder="Item description"
		value={item.description || ""}
		onChange={(e) => updateItem(item.id, "description", e.target.value)}
		rows={2}
									/>
								</td>

								<td className="td">
									<input
										className="invoice-input input-right"
										type="number"
										inputMode="decimal"
										placeholder="0.00"
										value={Number(item.rate) ? item.rate : ""}
										onChange={(e) => updateItem(item.id, "rate", parseFloat(e.target.value) || 0)}
									/>
								</td>

								<td className="td">
									<input
										className="invoice-input input-right"
										type="number"
										inputMode="numeric"
										min="1"
										placeholder="1"
										value={Number(item.qty) ? item.qty : ""}
										onChange={(e) => updateItem(item.id, "qty", parseInt(e.target.value, 10) || 1)}
									/>
								</td>

								<td className="td">
									<input
										className="invoice-input input-right input-muted"
										type="text"
										value={(Number(item.amount) || 0).toFixed(2)}
										readOnly
									/>
								</td>

								<td className="td td-actions">
									<button
										type="button"
										className="icon-btn"
										onClick={() => removeItem(item.id)}
										disabled={(items || []).length === 1}
										aria-label="Remove item"
										title={(items || []).length === 1 ? "At least one item is required" : "Remove item"}
									>
										<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
											<path d="M9 3a1 1 0 0 0-1 1v1H5a1 1 0 1 0 0 2h1v13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7h1a1 1 0 1 0 0-2h-3V4a1 1 0 0 0-1-1H9Zm1 2h4V5h-4v0ZM8 7h8v13H8V7Zm2 3a1 1 0 0 1 1 1v7a1 1 0 1 1-2 0v-7a1 1 0 0 1 1-1Zm5 1a1 1 0 0 0-2 0v7a1 1 0 1 0 2 0v-7Z" />
										</svg>
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<button type="button" className="add-btn" onClick={addItem}>
				<span className="add-btn__icon" aria-hidden="true">
					<svg viewBox="0 0 24 24" width="16" height="16">
						<path d="M12 5a1 1 0 0 1 1 1v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H6a1 1 0 1 1 0-2h5V6a1 1 0 0 1 1-1Z" />
					</svg>
				</span>
				Add Item
			</button>
		</div>
	);
}
